/**
 * Panneau « Notifications » du dock (bouton cloche) : 10 dernières notifications, lues et non lues.
 * Clic sur une notification = la marquer lue + ouvrir son objet (`lien`) selon la règle de l'espace de travail :
 * clic = dans la page active, Ctrl/⌘ + clic = nouvelle page. Pied : « Tout marquer comme lu », « Voir toutes les
 * notifications » (écran Mon profil › Notifications).
 */
import type { PageNotifications } from '@agora/shared';
import { NotificationsPanel } from '../../design';
import { ErreurApi, useFormats, useT } from '../../socle';
import { useActionsEspace } from '../pages/espace';
import { ROUTE_NOTIFICATIONS, iconeNotification, texteNotification, useActionsNotifications } from '.';

interface Props {
  page?: PageNotifications;
  chargement: boolean;
  erreur: unknown;
  /** Ferme le panneau (après une ouverture, ou Échap) ; le dock rend alors le focus à la cloche. */
  fermer: () => void;
}

export function PanneauNotifications({ page, chargement, erreur, fermer }: Props) {
  const { t } = useT();
  const f = useFormats();
  const espace = useActionsEspace();
  const actions = useActionsNotifications();
  const code = (e: unknown) => (e instanceof ErreurApi ? e.code : 'interne');
  const nonLues = page?.nonLues ?? 0;
  const elements = page?.elements ?? [];

  return (
    <NotificationsPanel
      title={t('notifications.titre')}
      countLabel={nonLues ? t('notifications.nonLues', { n: f.nombre(nonLues) }) : undefined}
      unreadLabel={t('notifications.nonLue')}
      emptyLabel={t('notifications.vide')}
      status={!page && chargement ? t('app.chargement') : !page && erreur ? t(`erreurs.${code(erreur)}`) : undefined}
      notice={actions.erreur ? t(`erreurs.${code(actions.erreur)}`) : undefined}
      items={elements.map((n) => ({
        id: n.id,
        icon: iconeNotification(n),
        message: texteNotification(n, t),
        author: n.auteur ?? t('notifications.systeme'),
        time: f.relatif(n.creeLe),
        dateTime: n.creeLe,
        dateLabel: f.dateHeure(n.creeLe),
        entity: n.filiale ?? undefined,
        unread: !n.lueLe,
      }))}
      onOpen={(id, e) => {
        const n = elements.find((x) => x.id === id);
        if (!n) return;
        actions.marquerLue(n);
        if (!n.lien) return;
        fermer();
        espace.ouvrir(n.lien, { evenement: e });
      }}
      markAllLabel={t('notifications.toutLire')}
      markAllDisabled={!nonLues || actions.toutEnCours}
      onMarkAll={actions.toutLire}
      seeAllLabel={t('notifications.voirTout')}
      onSeeAll={(e) => { fermer(); espace.ouvrir(ROUTE_NOTIFICATIONS, { evenement: e }); }}
      onClose={fermer}
    />
  );
}
